import { Button } from '@/components/ui/button';
import {
  LogOut,
  HelpCircle,
  Settings,
  User,
  Menu,
  Shield,
  Palette,
  Building2,
  Key,
  Tags,
  Layers,
  Users,
} from 'lucide-react';
import { SearchBar } from '@/components/layout/SearchBar';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useState, useMemo } from 'react';
import Avatar from '@/components/Avatar';
import { LogoIcon } from '@/components/Logo';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';

export function Header() {
  const { user, logout, checkPermission } = useAuth();
  const navigate = useNavigate();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { layout } = useProductDetailPageTranslations();
  const settingsI18n = useSettingspageTranslations();

  // Memoize user data to prevent unnecessary Avatar re-renders
  const memoizedUserData = useMemo(
    () => ({
      name: user?.name || layout.header.fallbacks.userName,
      email: user?.email || layout.header.fallbacks.userEmail,
      avatarUrl: user?.avatar_url,
    }),
    [user?.name, user?.email, user?.avatar_url, layout.header.fallbacks]
  );

  // Check permissions at the top level
  const lowerRole = user?.role?.toString().toLowerCase();
  const isOrgAdmin = lowerRole === 'admin' || lowerRole === 'owner' || lowerRole === 'orgowner';
  const hasSettingsViewPermission = checkPermission('settings.view');
  const hasSettingsChangePermission = checkPermission('settings.change');
  const hasSettingsPermission = hasSettingsViewPermission || hasSettingsChangePermission;

  // Check API access using unified logic
  const hasApiAccess = (() => {
    // Allow Django superusers (KernLogic staff)
    if (user?.is_superuser) return true;

    // Organization owners and admins have API access
    if (isOrgAdmin) return true;

    // Check for existing backend permissions
    if (checkPermission('settings.change')) return true;
    if (checkPermission('settings.view')) return true;

    return false;
  })();

  // Define and filter settings tabs using i18n and permissions
  const settingsTabs = useMemo(() => {
    // Define which tabs require admin permissions
    const adminOnlyTabs = ['organization', 'attributes', 'attribute-groups', 'families'];

    const allTabs = [
      { value: 'profile', label: settingsI18n.tabs.profile },
      { value: 'security', label: settingsI18n.tabs.security },
      { value: 'preferences', label: settingsI18n.tabs.preferences },
      { value: 'organization', label: settingsI18n.tabs.organization },
      { value: 'api', label: settingsI18n.tabs.api },
      { value: 'attributes', label: settingsI18n.tabs.attributes },
      { value: 'attribute-groups', label: settingsI18n.tabs.attributeGroups },
      { value: 'families', label: settingsI18n.tabs.families },
    ];

    // If user is staff (system admin), show all tabs
    if (user?.is_staff) {
      return allTabs;
    }

    return allTabs.filter(tab => {
      // Always show profile, security, and preferences to everyone
      if (['profile', 'security', 'preferences'].includes(tab.value)) {
        return true;
      }

      // API tab requires the same permissions as API content
      if (tab.value === 'api') {
        return hasApiAccess;
      }

      // Admin-only tabs require admin role or settings permissions
      if (adminOnlyTabs.includes(tab.value)) {
        return isOrgAdmin || hasSettingsPermission;
      }

      return true;
    });
  }, [settingsI18n.tabs, user?.is_staff, isOrgAdmin, hasSettingsPermission, hasApiAccess]);

  const handleDocumentationClick = () => {
    navigate('/app/documentation');
  };

  const handleSettingsClick = () => {
    navigate('/app/settings');
  };

  const handleSettingsTabClick = (tabValue: string) => {
    navigate(`/app/settings?tab=${tabValue}`);
  };

  // Icon mapping for settings tabs
  const getTabIcon = (tabValue: string) => {
    switch (tabValue) {
      case 'profile':
        return <User className="mr-2 size-4 text-enterprise-500" />;
      case 'security':
        return <Shield className="mr-2 size-4 text-enterprise-500" />;
      case 'preferences':
        return <Palette className="mr-2 size-4 text-enterprise-500" />;
      case 'organization':
        return <Building2 className="mr-2 size-4 text-enterprise-500" />;
      case 'api':
        return <Key className="mr-2 size-4 text-enterprise-500" />;
      case 'attributes':
        return <Tags className="mr-2 size-4 text-enterprise-500" />;
      case 'attribute-groups':
        return <Layers className="mr-2 size-4 text-enterprise-500" />;
      case 'families':
        return <Users className="mr-2 size-4 text-enterprise-500" />;
      default:
        return <Settings className="mr-2 size-4 text-enterprise-500" />;
    }
  };

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border bg-gradient-to-r from-background via-primary-50/30 to-secondary-50/30 px-4 shadow-sm backdrop-blur-sm dark:from-background dark:via-primary-900/20 dark:to-secondary-900/20 md:px-6">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="text-muted-foreground hover:text-primary md:hidden"
        onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      >
        <Menu className="size-5" />
      </Button>

      {/* Logo */}
      <a href="/app" className="group flex items-center gap-2">
        {/* Use only icon for both desktop and mobile */}
        <LogoIcon className="size-8 text-primary transition-colors duration-200 group-hover:text-primary/80" />
      </a>

      {/* Search */}
      <div className="hidden items-center gap-2 md:flex md:w-72">
        <SearchBar placeholder={layout.header.search.placeholder} className="w-full" />
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-1 md:gap-3">
        {/* Language Toggle */}
        <LanguageToggle />

        {/* Help/Documentation */}
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={handleDocumentationClick}
        >
          <HelpCircle className="size-5" />
        </Button>

        {/* Settings */}
        <Button
          variant="ghost"
          size="icon"
          className="hidden text-muted-foreground hover:bg-muted hover:text-foreground md:flex"
          onClick={handleSettingsClick}
        >
          <Settings className="size-5" />
        </Button>

        {/* User profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 px-2 text-foreground hover:bg-muted md:px-3"
            >
              <div className="relative">
                <Avatar
                  size="sm"
                  name={memoizedUserData.name}
                  avatarUrl={memoizedUserData.avatarUrl}
                  showUploadButton={false}
                />
              </div>
              <div className="hidden text-left md:block">
                <p className="text-sm font-medium text-foreground">{memoizedUserData.name}</p>
                <p className="text-xs text-muted-foreground">{memoizedUserData.email}</p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>{layout.header.userMenu.title}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {settingsTabs.map(tab => (
              <DropdownMenuItem
                key={tab.value}
                className="flex cursor-pointer items-center"
                onClick={() => handleSettingsTabClick(tab.value)}
              >
                {getTabIcon(tab.value)}
                <span>{tab.label}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer text-destructive" onClick={logout}>
              <LogOut className="mr-2 size-4" />
              <span>{layout.header.userMenu.logOut}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
